import { useEffect, useRef } from 'react'
import type { Locale } from '../content/site'
import {
  auditTheme,
  defaultTheme,
  presetNames,
  presets,
  type Theme,
} from '../lib/theme'

const LABELS = {
  title: { en: 'Appearance', ru: 'Оформление' },
  presets: { en: 'Presets', ru: 'Пресеты' },
  custom: { en: 'Custom colours', ru: 'Свои цвета' },
  background: { en: 'background', ru: 'фон' },
  foreground: { en: 'text', ru: 'текст' },
  accent: { en: 'accent', ru: 'акцент' },
  reset: { en: 'Reset', ru: 'Сброс' },
  close: { en: 'Close', ru: 'Закрыть' },
  lowContrast: {
    en: 'Low contrast — text may be hard to read.',
    ru: 'Низкий контраст — текст может быть плохо читаем.',
  },
} as const

export function ThemePanel({
  locale,
  theme,
  onTheme,
  onClose,
}: {
  locale: Locale
  theme: Theme
  onTheme: (theme: Theme) => void
  onClose: () => void
}) {
  const panelRef = useRef<HTMLDivElement>(null)
  const audit = auditTheme(theme)

  useEffect(() => {
    const panel = panelRef.current
    panel?.querySelector('button')?.focus()
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    const onPointer = (event: MouseEvent) => {
      if (panel && !panel.contains(event.target as Node)) onClose()
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onPointer)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onPointer)
    }
  }, [onClose])

  return (
    <div
      className="theme-panel"
      role="dialog"
      aria-modal="false"
      aria-label={LABELS.title[locale]}
      ref={panelRef}
      onClick={(event) => event.stopPropagation()}
    >
      <p className="theme-panel__heading" id="theme-presets-label">
        {LABELS.presets[locale]}
      </p>
      <div className="theme-panel__presets" role="group" aria-labelledby="theme-presets-label">
        {presetNames.map((name) => {
          const preset = presets[name]
          const active =
            preset.background === theme.background &&
            preset.foreground === theme.foreground &&
            preset.accent === theme.accent
          return (
            <button
              type="button"
              key={name}
              className={`theme-swatch${active ? ' theme-swatch--active' : ''}`}
              aria-pressed={active}
              onClick={() => onTheme(preset)}
            >
              <span
                className="theme-swatch__chip"
                aria-hidden="true"
                style={{ background: preset.background, borderColor: preset.accent }}
              >
                <span style={{ background: preset.foreground }} />
                <span style={{ background: preset.accent }} />
              </span>
              {name}
            </button>
          )
        })}
      </div>

      <p className="theme-panel__heading">{LABELS.custom[locale]}</p>
      <div className="theme-panel__colors">
        {(Object.keys(theme) as (keyof Theme)[]).map((name) => (
          <label key={name}>
            <input
              type="color"
              value={theme[name]}
              onChange={(event) => onTheme({ ...theme, [name]: event.target.value })}
            />
            {LABELS[name][locale]}
          </label>
        ))}
      </div>

      {!audit.readable && (
        <p className="theme-panel__warning" role="alert">
          ⚠ {LABELS.lowContrast[locale]} ({audit.foreground.toFixed(1)}:1)
        </p>
      )}

      <div className="theme-panel__actions">
        <button type="button" onClick={() => onTheme(defaultTheme)}>
          {LABELS.reset[locale]}
        </button>
        <button type="button" onClick={onClose}>
          {LABELS.close[locale]}
        </button>
      </div>
    </div>
  )
}
